import React from "react";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import { newSalespersonContact } from "../../../../../api";
import type { Job, Contact } from "../../../../../api/types";
import { getValidationErrorKey } from "../../../../../utilities/jobValidation";
import { CREATE_NEW_SALESPERSON } from "../../../../../utilities/constants";
import { columnDisplayNames } from "../../../../admin/ImportSettings";
import { ColumnContext } from "../standard-column-types";
import { useSalespersonColumnViewModel } from "./Salesperson.column.vm";
import { useValidationErrors } from "../../ValidationErrorsContext";

const filter = createFilterOptions<Contact>();

export interface SalespersonColumnMobileProps {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** Whether the field is required */
	required?: boolean;
	/** Whether the field is disabled */
	disabled?: boolean;
	/** Custom label override */
	label?: string;
}

/**
 * Mobile-friendly Salesperson column component.
 * Allows selection of an existing salesperson or creation of a new one.
 */
export const SalespersonColumnMobile: React.FC<SalespersonColumnMobileProps> = ({
	job,
	columnContext,
	required = false,
	disabled = false,
	label,
}) => {
	const vm = useSalespersonColumnViewModel(columnContext);
	const validationErrors = useValidationErrors();

	const allSalespersons = vm.getAllSalespersons();
	const selectedSalesperson = vm.getSelectedSalesperson(job);
	const errorKey = getValidationErrorKey("salesperson", job.id);

	const handleChange = (_event: React.SyntheticEvent, value: string | Contact | null, reason: string) => {
		if (disabled) return;

		// Ignore blur events
		if (reason === "blur") return;

		const newJobs = [...columnContext.jobs];
		const jobIndex = newJobs.findIndex((j) => j.id === job.id);
		if (jobIndex === -1) return;

		const newJob = { ...newJobs[jobIndex] };

		if (typeof value === "string") {
			// Handle free text input
			const newValue = value.startsWith("New Salesperson:") ? value.replace("New Salesperson: ", "") : value;

			if (newValue !== "") {
				// Check if salesperson already exists
				const existingSalesperson = allSalespersons.find((sp) => sp.name.toLowerCase() === newValue.toLowerCase());

				if (existingSalesperson) {
					newJob.salespersonIds = [existingSalesperson.id];
					newJob.newSalesperson = undefined;
				} else {
					newJob.salespersonIds = [CREATE_NEW_SALESPERSON];
					newJob.newSalesperson = { name: newValue, phone: "" };

					// Add to local salespersons list
					const tempId = `temp-${Date.now()}-${Math.random()}`;
					const firstName = newValue.split(" ").at(0) || "";
					const lastName = newValue.split(" ").slice(1).join(" ") || "";
					const newSalesperson = newSalespersonContact(tempId, newValue, firstName, lastName);

					if (!columnContext.newSalespersons.some((sp) => sp.name.toLowerCase() === newValue.toLowerCase())) {
						columnContext.setNewSalespersons([...columnContext.newSalespersons, newSalesperson]);
					}
				}
			}
		} else if (value && value.id === CREATE_NEW_SALESPERSON) {
			const name = value.name.replace("New Salesperson: ", "");
			const existingSalesperson = allSalespersons.find((sp) => sp.name.toLowerCase() === name.toLowerCase());

			if (existingSalesperson) {
				newJob.salespersonIds = [existingSalesperson.id];
				newJob.newSalesperson = undefined;
			} else {
				newJob.salespersonIds = [CREATE_NEW_SALESPERSON];
				newJob.newSalesperson = { name, phone: "" };

				const tempId = `temp-${Date.now()}-${Math.random()}`;
				const firstName = name.split(" ").at(0) || "";
				const lastName = name.split(" ").slice(1).join(" ") || "";
				const newSalesperson = newSalespersonContact(tempId, name, firstName, lastName);

				if (!columnContext.newSalespersons.some((sp) => sp.name.toLowerCase() === name.toLowerCase())) {
					columnContext.setNewSalespersons([...columnContext.newSalespersons, newSalesperson]);
				}
			}
		} else if (value && value.id !== CREATE_NEW_SALESPERSON) {
			if (columnContext.isNewSalesperson(value.id)) {
				const localSp = columnContext.findNewSalesperson(value.id);
				if (localSp) {
					newJob.salespersonIds = [CREATE_NEW_SALESPERSON];
					newJob.newSalesperson = { name: localSp.name, phone: localSp.mobilePhone };
				}
			} else {
				newJob.salespersonIds = [value.id];
				newJob.newSalesperson = undefined;
			}
		} else if (value === null && reason === "clear") {
			newJob.salespersonIds = [];
			newJob.newSalesperson = undefined;
		} else {
			return;
		}

		newJobs[jobIndex] = newJob;
		columnContext.setJobs(newJobs);
		columnContext.validateJobs([newJob], { updateState: true, clearPrevious: false });
	};

	return (
		<Autocomplete
			size="small"
			fullWidth
			freeSolo
			disabled={disabled}
			value={selectedSalesperson}
			options={allSalespersons}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			getOptionLabel={(option) => {
				if (typeof option === "string") {
					return option.startsWith("New Salesperson") ? option : `New Salesperson: ${option}`;
				}
				return option?.name ?? "";
			}}
			filterOptions={(options, params) => {
				const filtered = filter(options, params);

				if (
					params.inputValue !== "" &&
					!allSalespersons.some((salesperson) => salesperson.name.toLowerCase() === params.inputValue.toLowerCase())
				) {
					filtered.push(newSalespersonContact(CREATE_NEW_SALESPERSON, `New Salesperson: ${params.inputValue}`));
				}

				return filtered;
			}}
			onChange={handleChange}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label || columnDisplayNames.salesperson}
					required={required}
					error={!!validationErrors?.[errorKey]}
					helperText={validationErrors?.[errorKey]}
				/>
			)}
		/>
	);
};
